import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Route-specific default SEO metadata configuration
const routeMetadata = {
  "/": {
    title: "DAZBOG | Renewable Energy Advisory, Utility Solar & EPC Solutions",
    description:
      "Dazbog is a premier integrated clean energy advisory and project execution partner, delivering bankable utility solar, wind-solar hybrid, battery energy storage (BESS), and EPC supervision across India.",
    keywords:
      "Dazbog, renewable energy advisory, utility scale solar, solar energy India, clean energy EPC, battery energy storage, BESS, project finance solar, green hydrogen, solar consultancy",
  },
  "/about": {
    title: "About Us | DAZBOG - Renewable Energy Advisory & Clean Tech",
    description:
      "Discover Dazbog's mission, leadership, core values, and proven expertise in advancing utility-scale solar, energy storage, and clean technology infrastructure.",
    keywords:
      "About Dazbog, solar advisory firm, clean energy leadership, renewable energy consultants, solar engineering expertise, sustainable power advisory",
  },
  "/services": {
    title: "Clean Energy Services & Solutions | DAZBOG",
    description:
      "Comprehensive renewable energy services: financial consultancy, technical engineering due diligence, turnkey EPC coordination, and BESS storage solutions.",
    keywords:
      "renewable energy services, solar finance advisory, EPC execution, BESS energy storage, technical engineering diligence, clean energy solutions",
  },
  "/projects": {
    title: "Renewable Energy Projects & Case Studies | DAZBOG",
    description:
      "Explore Dazbog's proven utility-scale solar parks, wind-solar hybrids, and battery energy storage (BESS) project execution portfolio.",
    keywords:
      "solar projects portfolio, utility solar parks, wind-solar hybrid projects, BESS case studies, renewable energy execution India",
  },
  "/fullteam": {
    title: "Leadership & Expert Team | DAZBOG Clean Energy",
    description:
      "Meet our multidisciplinary team of clean energy engineers, financial advisors, and project execution leaders driving India's renewable energy transition.",
    keywords:
      "Dazbog leadership, clean energy experts, solar advisory team, renewable engineering professionals",
  },
  "/contact": {
    title: "Contact Us | Discuss Your Renewable Project | DAZBOG",
    description:
      "Get in touch with Dazbog clean energy specialists for utility solar consulting, project finance, EPC execution, and energy storage solutions.",
    keywords:
      "contact Dazbog, solar advisory contact, renewable energy inquiries, clean energy consultation India",
  },
};

const setMetaTag = (attr, key, content) => {
  if (!content) return;
  let element = document.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const PageMeta = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    
    // Match route or fallback to slug-based / generic
    let meta = routeMetadata[pathname];
    
    if (!meta) {
      if (pathname.startsWith("/services") || pathname.startsWith("/service")) {
        const slug = pathname.split("/").filter(Boolean).pop() || "";
        const formattedTitle = slug
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        meta = {
          title: `${formattedTitle || "Service Details"} | DAZBOG Services`,
          description: `Specialized renewable advisory and execution details for ${formattedTitle || "clean energy solutions"}.`,
          keywords: "renewable energy advisory, solar services, EPC, BESS",
        };
      } else if (pathname.startsWith("/projects") || pathname.startsWith("/project")) {
        const slug = pathname.split("/").filter(Boolean).pop() || "";
        const formattedTitle = slug
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        meta = {
          title: `${formattedTitle || "Project Details"} | DAZBOG Projects`,
          description: `Case study and execution breakdown for ${formattedTitle || "clean energy project"}.`,
          keywords: "clean energy projects, solar park, hybrid renewable",
        };
      } else {
        meta = routeMetadata["/"];
      }
    }

    const currentUrl = `https://dazbog.ascentbw.com${pathname}`;
    const defaultOgImage = "https://dazbog.ascentbw.com/og-image.jpg";

    // Update document title
    document.title = meta.title;

    // Update standard meta tags
    setMetaTag("name", "title", meta.title);
    setMetaTag("name", "description", meta.description);
    if (meta.keywords) {
      setMetaTag("name", "keywords", meta.keywords);
    }

    // Update Open Graph tags
    setMetaTag("property", "og:title", meta.title);
    setMetaTag("property", "og:description", meta.description);
    setMetaTag("property", "og:url", currentUrl);
    setMetaTag("property", "og:site_name", "DAZBOG Clean Energy");
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:image", defaultOgImage);

    // Update Twitter Cards
    setMetaTag("name", "twitter:title", meta.title);
    setMetaTag("name", "twitter:description", meta.description);
    setMetaTag("name", "twitter:url", currentUrl);
    setMetaTag("name", "twitter:image", defaultOgImage);

    // Update Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);
  }, [location.pathname]);

  return null;
};

export default PageMeta;
